import { createQueryParams } from "features/config/helpers";
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { IconButton, Button, Tooltip, Checkbox } from "@mui/material";
import {
  FaEye,
  FaMoneyBillWave,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaLayerGroup,
} from "react-icons/fa";
import { CiFilter } from "react-icons/ci";
import { MdPayment } from "react-icons/md";
import { AiOutlineFileDone } from "react-icons/ai";

import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import {
  getPaymentOrders,
  resetGetPaymentOrders,
  selectGetPaymentOrders,
  GetPaymentOrdersState,
} from "../slices/get-payment-orders.slice";
import {
  Column,
  DataTable,
  DataTableCell,
  DataTableRow,
} from "features/shared/presentation/components/data-table";
import { DataList } from "features/shared/presentation/components";
import { dateSetup } from "./stock-ordering-utils";
import { OrderFilter } from ".";
import { PaymentProcessModal } from "features/stock-ordering/presentation/modals/payment-process.modal";
import { BatchPaymentModal } from "features/stock-ordering/presentation/modals/batch-payment.modal";
import { BatchPaymentConfirmationModal } from "features/stock-ordering/presentation/modals/batch-payment-confirmation.modal";

interface DataFilterData {
  type?: string | null;
  start?: string | null;
  end?: string | null;
}

// Deadline urgency helpers
type UrgencyLevel = "normal" | "amber" | "red" | "overdue";

const getUrgencyLevel = (daysRemaining: number | null): UrgencyLevel => {
  if (daysRemaining === null) return "normal";
  if (daysRemaining < 0) return "overdue";
  if (daysRemaining <= 7) return "red";
  if (daysRemaining <= 15) return "amber";
  return "normal";
};

const getRowBgClass = (urgency: UrgencyLevel): string => {
  switch (urgency) {
    case "overdue":
      return "bg-red-50";
    case "red":
      return "bg-red-50";
    case "amber":
      return "bg-amber-50";
    default:
      return "";
  }
};

const getDeadlineBadge = (daysRemaining: number | null) => {
  if (daysRemaining === null) return null;

  const urgency = getUrgencyLevel(daysRemaining);

  switch (urgency) {
    case "overdue":
      return (
        <div className="flex items-center space-x-1.5">
          <FaExclamationCircle className="text-red-600 text-sm animate-pulse" />
          <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">
            OVERDUE
          </span>
        </div>
      );
    case "red":
      return (
        <div className="flex items-center space-x-1.5">
          <FaExclamationCircle className="text-red-500 text-sm" />
          <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">
            {daysRemaining === 0 ? "Due Today" : `${daysRemaining}d left`}
          </span>
        </div>
      );
    case "amber":
      return (
        <div className="flex items-center space-x-1.5">
          <FaExclamationTriangle className="text-amber-500 text-sm" />
          <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
            {daysRemaining}d left
          </span>
        </div>
      );
    default:
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          {daysRemaining}d left
        </span>
      );
  }
};

const getMobileDeadlineBadge = (daysRemaining: number | null) => {
  if (daysRemaining === null) return null;

  const urgency = getUrgencyLevel(daysRemaining);

  switch (urgency) {
    case "overdue":
      return (
        <span className="flex items-center space-x-1 px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-700">
          <FaExclamationCircle className="animate-pulse" />
          <span>OVERDUE</span>
        </span>
      );
    case "red":
      return (
        <span className="flex items-center space-x-1 px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-700">
          <FaExclamationCircle />
          <span>{daysRemaining === 0 ? "Due Today" : `${daysRemaining}d`}</span>
        </span>
      );
    case "amber":
      return (
        <span className="flex items-center space-x-1 px-2 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
          <FaExclamationTriangle />
          <span>{daysRemaining}d left</span>
        </span>
      );
    default:
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          {daysRemaining}d left
        </span>
      );
  }
};

export function PaymentContents() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { view, orderId } = useParams();

  // Selectors
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const getPaymentOrdersState = useAppSelector(selectGetPaymentOrders);

  // Determine if user is a seller/supplier
  const isSeller =
    (getAdminSessionState.data?.admin.user_details.sos_groups_seller.length ??
      0) > 0;

  // Active tab: 0 = For Payment, 1 = For Confirmation, 2 = Completed
  // Sellers default to tab 1 (For Confirmation)
  const [activeTab, setActiveTab] = useState(isSeller ? 1 : 0);

  // Modal state
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [currentPaymentTab, setCurrentPaymentTab] = useState(0);

  // Filter trigger state
  const [openFilter, setOpenFilter] = useState<HTMLButtonElement | null>(null);

  // Batch selection state (only for tab 0, non-sellers)
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(
    new Set(),
  );
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

  // Batch confirmation/view modal state (for tabs 1 & 2)
  const [isBatchConfirmModalOpen, setIsBatchConfirmModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [batchModalReadOnly, setBatchModalReadOnly] = useState(false);
  const [batchModalIsCompleted, setBatchModalIsCompleted] = useState(false);

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedOrderIds(new Set());
  }, [activeTab]);

  // Query Parameters
  const query = useQuery();
  const pageNo = query.get("page_no");
  const perPage = query.get("per_page");
  const orderBy = query.get("order_by");
  const order = query.get("order");
  const search = query.get("search");
  const dateType = query.get("date_type");
  const startDate = query.get("start_date");
  const endDate = query.get("end_date");

  // Local search state for responsive typing (debounced)
  const [localSearch, setLocalSearch] = useState(search ?? "");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local search when URL search param changes externally (e.g. filter reset)
  useEffect(() => {
    setLocalSearch(search ?? "");
  }, [search]);

  // Debounced search: updates URL params after user stops typing
  const handleSearchChange = useCallback(
    (val: string) => {
      setLocalSearch(val);

      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      searchTimerRef.current = setTimeout(() => {
        const params = createQueryParams({
          page_no: 0,
          per_page: perPage,
          order_by: orderBy,
          order: order,
          search: val === "" ? null : val,
          date_type: dateType,
          start_date: startDate,
          end_date: endDate,
        });
        navigate({ pathname: "", search: params });
      }, 500);
    },
    [navigate, perPage, orderBy, order, dateType, startDate, endDate],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // Whether user can select orders for batch payment (tab 0, non-sellers only)
  const canSelectOrders = activeTab === 0 && !isSeller;

  // Table columns for "For Payment" tab
  const forPaymentColumns: Array<Column> = [
    ...(canSelectOrders ? [{ id: "select" as const, label: "" }] : []),
    { id: "store_name", label: "Store/Company" },
    { id: "supplier_name", label: "Supplier" },
    { id: "po_number", label: "Order Number" },
    { id: "total_cost", label: "Amount" },
    { id: "actual_delivery_date", label: "Delivery Date" },
    { id: "days_remaining", label: "Deadline" },
    { id: "action", label: "Action" },
  ];

  // Table columns for "For Confirmation" tab
  const forConfirmationColumns: Array<Column> = [
    { id: "store_name", label: "Store/Company" },
    { id: "supplier_name", label: "Supplier" },
    { id: "po_number", label: "Order Number" },
    { id: "total_cost", label: "Amount" },
    { id: "payment_proof_uploaded_at", label: "Date Submitted" },
    { id: "days_remaining", label: "Deadline" },
    { id: "action", label: "Action" },
  ];

  // Table columns for "Completed" tab
  const completedColumns: Array<Column> = [
    { id: "store_name", label: "Store/Company" },
    { id: "supplier_name", label: "Supplier" },
    { id: "po_number", label: "Order Number" },
    { id: "total_cost", label: "Amount" },
    { id: "confirmed_at", label: "Date Confirmed" },
    { id: "action", label: "Action" },
  ];

  // Fetch payment orders - activeTab maps directly to backend tab (0, 1, 2)
  useEffect(() => {
    dispatch(
      getPaymentOrders({
        tab: activeTab,
        search: search ?? undefined,
        view: isSeller ? "selling" : "order",
        pageNo: Number(pageNo) || 0,
        perPage: Number(perPage) || 25,
        orderBy: orderBy ?? "actual_delivery_date",
        order: order ?? "desc",
        dateType: dateType ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
      }),
    );

    return () => {
      dispatch(resetGetPaymentOrders());
    };
  }, [
    dispatch,
    search,
    isSeller,
    activeTab,
    pageNo,
    perPage,
    orderBy,
    order,
    dateType,
    startDate,
    endDate,
  ]);

  // Open modal if orderId is present
  useEffect(() => {
    if (orderId) {
      setIsProcessModalOpen(true);
    }
  }, [orderId]);

  // Handle order click
  const handleOrderClicked = (id: string, paymentStageId?: number) => () => {
    const queryParams = createQueryParams({
      page_no: pageNo,
      per_page: perPage,
      order_by: orderBy,
      order: order,
      search: search,
      date_type: dateType,
      start_date: startDate,
      end_date: endDate,
    });

    setCurrentPaymentTab(paymentStageId ? paymentStageId - 1 : 0);

    navigate({
      pathname: `/admin/material/payments/${id}`,
      search: queryParams,
    });

    setIsProcessModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsProcessModalOpen(false);

    const queryParams = createQueryParams({
      page_no: pageNo,
      per_page: perPage,
      order_by: orderBy,
      order: order,
      search: search,
      date_type: dateType,
      start_date: startDate,
      end_date: endDate,
    });

    navigate({
      pathname: `/admin/material/payments`,
      search: queryParams,
    });

    dispatch(
      getPaymentOrders({
        tab: activeTab,
        search: search ?? undefined,
        view: isSeller ? "selling" : "order",
        pageNo: Number(pageNo) || 0,
        perPage: Number(perPage) || 25,
        orderBy: orderBy ?? "actual_delivery_date",
        order: order ?? "desc",
        dateType: dateType ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
      }),
    );
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  // Get orders and tab counts from state
  const orders = getPaymentOrdersState.data?.orders ?? [];
  const tabCounts = getPaymentOrdersState.data?.tab ?? [0, 0, 0];
  const pagination = getPaymentOrdersState.data?.pagination;
  const totalOrders = pagination?.total_rows ?? orders.length;

  // Selection helper functions
  const handleToggleOrder = (orderId: string) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedOrderIds.size === orders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(orders.map((o: any) => o.id.toString())));
    }
  };

  const selectedOrders = orders.filter((o: any) =>
    selectedOrderIds.has(o.id.toString()),
  );

  const selectedTotal = selectedOrders.reduce(
    (sum: number, o: any) => sum + Number(o.total_cost || 0),
    0,
  );

  // Batch grouping helper for tabs 1 & 2
  const groupOrdersByBatch = (orderList: any[]) => {
    const batched: Record<string, any[]> = {};
    const unbatched: any[] = [];

    orderList.forEach((order) => {
      if (order.payment_batch_id) {
        const key = order.payment_batch_id.toString();
        if (!batched[key]) batched[key] = [];
        batched[key].push(order);
      } else {
        unbatched.push(order);
      }
    });

    const rows: any[] = [];

    Object.entries(batched).forEach(([batchId, batchOrders]) => {
      rows.push({
        _isBatchGroup: true,
        _batchId: batchId,
        _batchOrders: batchOrders,
        batch_code: batchOrders[0].batch_code,
        batch_order_count: batchOrders.length,
        batch_total_amount: batchOrders.reduce(
          (sum: number, o: any) => sum + Number(o.total_cost || 0),
          0,
        ),
        store_name:
          batchOrders.length === 1
            ? batchOrders[0].store_name
            : `${batchOrders.length} stores`,
        supplier_name: batchOrders[0].supplier_name,
        id: `batch-${batchId}`,
        payment_stage_id: batchOrders[0].payment_stage_id,
        days_remaining: Math.min(
          ...batchOrders.map((o: any) =>
            o.days_remaining !== null ? Number(o.days_remaining) : 999,
          ),
        ),
        payment_deadline: batchOrders[0].payment_deadline,
        payment_proof_uploaded_at: batchOrders[0].payment_proof_uploaded_at,
        confirmed_at: batchOrders[0].confirmed_at,
      });
    });

    rows.push(...unbatched);
    return rows;
  };

  // Get display rows: for tabs 1 & 2, group batched orders
  const displayRows = activeTab === 0 ? orders : groupOrdersByBatch(orders);

  const payCount = tabCounts[0];
  const confirmCount = tabCounts[1];
  const completedCount = tabCounts[2];

  // Get the right columns based on active tab
  const getColumns = () => {
    switch (activeTab) {
      case 0:
        return forPaymentColumns;
      case 1:
        return forConfirmationColumns;
      case 2:
        return completedColumns;
      default:
        return forPaymentColumns;
    }
  };

  // Get stats card text
  const getStatsText = () => {
    switch (activeTab) {
      case 0:
        return "Orders Awaiting Payment";
      case 1:
        return "Payments Awaiting Confirmation";
      case 2:
        return "Completed Payments";
      default:
        return "Orders";
    }
  };

  // Get empty message
  const getEmptyMessage = () => {
    switch (activeTab) {
      case 0:
        return "No orders awaiting payment.";
      case 1:
        return "No payments awaiting confirmation.";
      case 2:
        return "No completed payments.";
      default:
        return "No orders found.";
    }
  };

  // Tab configuration
  const tabs = [
    {
      id: 0,
      label: "For Payment",
      icon: <MdPayment className="text-lg" />,
      count: payCount,
      activeClass: "bg-yellow-500 text-white shadow-md",
      badgeActiveClass: "bg-yellow-400 text-yellow-900",
      badgeInactiveClass: "bg-yellow-100 text-yellow-700",
      statsIconBg: "bg-yellow-100",
      statsIcon: <MdPayment className="text-yellow-600 text-xl" />,
    },
    {
      id: 1,
      label: "For Confirmation",
      icon: <AiOutlineFileDone className="text-lg" />,
      count: confirmCount,
      activeClass: "bg-blue-500 text-white shadow-md",
      badgeActiveClass: "bg-blue-400 text-blue-900",
      badgeInactiveClass: "bg-blue-100 text-blue-700",
      statsIconBg: "bg-blue-100",
      statsIcon: <AiOutlineFileDone className="text-blue-600 text-xl" />,
    },
    {
      id: 2,
      label: "Completed",
      icon: <FaCheckCircle className="text-lg" />,
      count: completedCount,
      activeClass: "bg-green-600 text-white shadow-md",
      badgeActiveClass: "bg-green-500 text-green-100",
      badgeInactiveClass: "bg-green-100 text-green-700",
      statsIconBg: "bg-green-100",
      statsIcon: <FaCheckCircle className="text-green-600 text-xl" />,
    },
  ];

  const currentTabConfig = tabs[activeTab];
  const columns = getColumns();

  // Render the date column based on active tab
  const renderDateCell = (paymentOrder: any) => {
    switch (activeTab) {
      case 0:
        return (
          <DataTableCell>
            <span className="text-gray-600">
              {paymentOrder.actual_delivery_date
                ? dateSetup(paymentOrder.actual_delivery_date, false)
                : "N/A"}
            </span>
          </DataTableCell>
        );
      case 1:
        return (
          <DataTableCell>
            <span className="text-gray-600">
              {paymentOrder.payment_proof_uploaded_at
                ? dateSetup(paymentOrder.payment_proof_uploaded_at, false)
                : "N/A"}
            </span>
          </DataTableCell>
        );
      case 2:
        return (
          <DataTableCell>
            <span className="text-gray-600">
              {paymentOrder.confirmed_at
                ? dateSetup(paymentOrder.confirmed_at, false)
                : "N/A"}
            </span>
          </DataTableCell>
        );
      default:
        return null;
    }
  };

  // Render deadline cell for pending tabs (0 and 1)
  const renderDeadlineCell = (paymentOrder: any) => {
    const daysRemaining =
      paymentOrder.days_remaining !== null &&
      paymentOrder.days_remaining !== undefined
        ? Number(paymentOrder.days_remaining)
        : null;

    const deadlineDate = paymentOrder.payment_deadline;

    return (
      <DataTableCell>
        <Tooltip
          title={
            deadlineDate
              ? `Deadline: ${dateSetup(deadlineDate, false)}`
              : "No deadline"
          }
          arrow
          placement="top"
        >
          <div>{getDeadlineBadge(daysRemaining)}</div>
        </Tooltip>
      </DataTableCell>
    );
  };

  // Render mobile date info based on active tab
  const renderMobileDateInfo = (paymentOrder: any) => {
    switch (activeTab) {
      case 0:
        return paymentOrder.actual_delivery_date
          ? dateSetup(paymentOrder.actual_delivery_date, false)
          : "N/A";
      case 1:
        return paymentOrder.payment_proof_uploaded_at
          ? `Submitted: ${dateSetup(paymentOrder.payment_proof_uploaded_at, false)}`
          : "N/A";
      case 2:
        return paymentOrder.confirmed_at
          ? `Confirmed: ${dateSetup(paymentOrder.confirmed_at, false)}`
          : "N/A";
      default:
        return "N/A";
    }
  };

  // Get urgency for a row
  const getRowUrgency = (paymentOrder: any): UrgencyLevel => {
    if (activeTab === 2) return "normal"; // No urgency for completed
    const daysRemaining =
      paymentOrder.days_remaining !== null &&
      paymentOrder.days_remaining !== undefined
        ? Number(paymentOrder.days_remaining)
        : null;
    return getUrgencyLevel(daysRemaining);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-green-100 p-2 rounded-lg">
              <FaMoneyBillWave className="text-2xl text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Payments</h1>
              <p className="text-gray-600">
                Manage payment submissions and confirmations
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? tab.activeClass
                  : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span
                  className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full ${
                    activeTab === tab.id
                      ? tab.badgeActiveClass
                      : tab.badgeInactiveClass
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className={`p-3 rounded-full ${currentTabConfig.statsIconBg}`}
              >
                {currentTabConfig.statsIcon}
              </div>
              <div>
                <p className="text-sm text-gray-500">{getStatsText()}</p>
                <p className="text-2xl font-bold text-gray-800">
                  {totalOrders}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              {/* Select All toggle (only for tab 0, non-sellers) */}
              {canSelectOrders && orders.length > 0 && (
                <Button
                  variant={selectedOrderIds.size > 0 ? "contained" : "outlined"}
                  size="small"
                  onClick={handleToggleAll}
                  sx={
                    selectedOrderIds.size > 0
                      ? {
                          backgroundColor: "#16a34a",
                          "&:hover": { backgroundColor: "#15803d" },
                        }
                      : {
                          borderColor: "#16a34a",
                          color: "#16a34a",
                          "&:hover": {
                            borderColor: "#15803d",
                            backgroundColor: "rgba(22, 163, 74, 0.04)",
                          },
                        }
                  }
                >
                  {selectedOrderIds.size === orders.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<CiFilter />}
                onClick={(e) => setOpenFilter(e.currentTarget)}
                sx={{
                  borderColor: "#16a34a",
                  color: "#16a34a",
                  "&:hover": {
                    borderColor: "#15803d",
                    backgroundColor: "rgba(22, 163, 74, 0.04)",
                  },
                }}
              >
                Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {getPaymentOrdersState.status === GetPaymentOrdersState.success ? (
          <div className="bg-white rounded-xl shadow-sm">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <DataTable
                columns={columns}
                order={order === "asc" ? "asc" : "desc"}
                orderBy={orderBy ?? "actual_delivery_date"}
                search={localSearch}
                emptyMessage={getEmptyMessage()}
                onSearch={(val) => handleSearchChange(val)}
                onRequestSort={(col) => {
                  const isAsc = orderBy === col && order === "asc";
                  const params = createQueryParams({
                    page_no: 0,
                    per_page: perPage,
                    order_by: col,
                    order: isAsc ? "desc" : "asc",
                    search: search,
                    date_type: dateType,
                    start_date: startDate,
                    end_date: endDate,
                  });
                  navigate({ pathname: "", search: params });
                }}
                onRowsPerPageChange={(event) => {
                  const params = createQueryParams({
                    page_no: 0,
                    per_page: event.target.value,
                    order_by: orderBy,
                    order: order,
                    search: search,
                    date_type: dateType,
                    start_date: startDate,
                    end_date: endDate,
                  });
                  navigate({ pathname: "", search: params });
                }}
                onPageChange={(event, newPage) => {
                  const params = createQueryParams({
                    page_no: newPage,
                    per_page: perPage,
                    order_by: orderBy,
                    order: order,
                    search: search,
                    date_type: dateType,
                    start_date: startDate,
                    end_date: endDate,
                  });
                  navigate({ pathname: "", search: params });
                }}
                totalRows={totalOrders}
                perPage={Number(perPage) || 25}
                page={pageNo ? parseInt(pageNo) : 1}
              >
                {displayRows.map((paymentOrder: any) => {
                  // Batch group row
                  if (paymentOrder._isBatchGroup) {
                    return (
                      <DataTableRow
                        key={paymentOrder.id}
                        sx={{
                          backgroundColor: "#f0fdf4",
                          "&:hover": {
                            backgroundColor: "#dcfce7 !important",
                          },
                        }}
                      >
                        <DataTableCell>
                          <div className="flex items-center space-x-2">
                            <FaLayerGroup className="text-green-600 text-sm flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-gray-800">
                                Batch Payment
                              </span>
                              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-700">
                                {paymentOrder.batch_order_count} orders
                              </span>
                            </div>
                          </div>
                        </DataTableCell>
                        <DataTableCell>
                          <span className="text-gray-700">
                            {paymentOrder.supplier_name}
                          </span>
                        </DataTableCell>
                        <DataTableCell>
                          <span className="font-medium text-green-700">
                            {paymentOrder.batch_code}
                          </span>
                        </DataTableCell>
                        <DataTableCell>
                          <span className="font-semibold text-green-700">
                            {formatCurrency(paymentOrder.batch_total_amount)}
                          </span>
                        </DataTableCell>
                        {renderDateCell(paymentOrder)}
                        {activeTab !== 2 && renderDeadlineCell(paymentOrder)}
                        <DataTableCell>
                          <IconButton
                            onClick={() => {
                              // Open batch confirmation/view modal
                              const bId = Number(paymentOrder._batchId);
                              setSelectedBatchId(bId);
                              // Store users see read-only on tab 1; all users see read-only on tab 2
                              setBatchModalReadOnly(
                                !isSeller || activeTab === 2,
                              );
                              setBatchModalIsCompleted(activeTab === 2);
                              setIsBatchConfirmModalOpen(true);
                            }}
                            className="text-green-600 hover:text-green-800"
                          >
                            <FaEye />
                          </IconButton>
                        </DataTableCell>
                      </DataTableRow>
                    );
                  }

                  // Regular individual order row
                  const urgency = getRowUrgency(paymentOrder);
                  const rowBgClass = getRowBgClass(urgency);

                  return (
                    <DataTableRow
                      key={paymentOrder.id}
                      sx={{
                        backgroundColor: selectedOrderIds.has(
                          paymentOrder.id.toString(),
                        )
                          ? "#dbeafe"
                          : urgency === "overdue" || urgency === "red"
                            ? "#fef2f2"
                            : urgency === "amber"
                              ? "#fffbeb"
                              : undefined,
                        "&:hover": {
                          backgroundColor: selectedOrderIds.has(
                            paymentOrder.id.toString(),
                          )
                            ? "#bfdbfe !important"
                            : urgency === "overdue" || urgency === "red"
                              ? "#fee2e2 !important"
                              : urgency === "amber"
                                ? "#fef3c7 !important"
                                : undefined,
                        },
                      }}
                    >
                      {/* Checkbox for batch selection (tab 0, non-sellers only) */}
                      {canSelectOrders && (
                        <DataTableCell>
                          <Checkbox
                            size="small"
                            checked={selectedOrderIds.has(
                              paymentOrder.id.toString(),
                            )}
                            onChange={() =>
                              handleToggleOrder(paymentOrder.id.toString())
                            }
                            sx={{
                              color: "#16a34a",
                              "&.Mui-checked": { color: "#16a34a" },
                            }}
                          />
                        </DataTableCell>
                      )}
                      <DataTableCell>
                        <div className="flex items-center space-x-2">
                          {/* Warning icon for urgent rows */}
                          {urgency === "overdue" && (
                            <Tooltip title="Payment overdue!" arrow>
                              <span>
                                <FaExclamationCircle className="text-red-600 text-sm animate-pulse flex-shrink-0" />
                              </span>
                            </Tooltip>
                          )}
                          {urgency === "red" && (
                            <Tooltip title="Payment due very soon!" arrow>
                              <span>
                                <FaExclamationCircle className="text-red-500 text-sm flex-shrink-0" />
                              </span>
                            </Tooltip>
                          )}
                          {urgency === "amber" && (
                            <Tooltip title="Payment deadline approaching" arrow>
                              <span>
                                <FaExclamationTriangle className="text-amber-500 text-sm flex-shrink-0" />
                              </span>
                            </Tooltip>
                          )}
                          <span className="font-semibold text-gray-800">
                            {paymentOrder.store_name}
                          </span>
                        </div>
                      </DataTableCell>
                      <DataTableCell>
                        <span className="text-gray-700">
                          {paymentOrder.supplier_name}
                        </span>
                      </DataTableCell>
                      <DataTableCell>
                        <span className="font-medium text-gray-800">
                          {paymentOrder.po_number || `ORD-${paymentOrder.id}`}
                        </span>
                      </DataTableCell>
                      <DataTableCell>
                        <span className="font-semibold text-green-700">
                          {formatCurrency(paymentOrder.total_cost)}
                        </span>
                      </DataTableCell>
                      {renderDateCell(paymentOrder)}
                      {/* Deadline column for pending tabs only */}
                      {activeTab !== 2 && renderDeadlineCell(paymentOrder)}
                      <DataTableCell>
                        <IconButton
                          onClick={handleOrderClicked(
                            paymentOrder.id.toString(),
                            paymentOrder.payment_stage_id,
                          )}
                          className="text-green-600 hover:text-green-800"
                        >
                          <FaEye />
                        </IconButton>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTable>
            </div>

            {/* Mobile List */}
            <div className="md:hidden">
              <DataList
                search={localSearch}
                emptyMessage={getEmptyMessage()}
                onSearch={(val) => handleSearchChange(val)}
                onPageChange={(event, newPage) => {
                  const params = createQueryParams({
                    page_no: newPage,
                    per_page: perPage,
                    order_by: orderBy,
                    order: order,
                    search: search,
                    date_type: dateType,
                    start_date: startDate,
                    end_date: endDate,
                  });
                  navigate({ pathname: "", search: params });
                }}
                onRowsPerPageChange={(event) => {
                  const params = createQueryParams({
                    page_no: 0,
                    per_page: event.target.value,
                    order_by: orderBy,
                    order: order,
                    search: search,
                    date_type: dateType,
                    start_date: startDate,
                    end_date: endDate,
                  });
                  navigate({ pathname: "", search: params });
                }}
                totalRows={totalOrders}
                perPage={Number(perPage) || 25}
                page={pageNo ? parseInt(pageNo) : 1}
              >
                <div className="space-y-4 p-4">
                  {orders.map((paymentOrder: any) => {
                    const urgency = getRowUrgency(paymentOrder);
                    const daysRemaining =
                      paymentOrder.days_remaining !== null &&
                      paymentOrder.days_remaining !== undefined
                        ? Number(paymentOrder.days_remaining)
                        : null;

                    const cardBorderClass =
                      urgency === "overdue" || urgency === "red"
                        ? "border-red-300 bg-red-50"
                        : urgency === "amber"
                          ? "border-amber-300 bg-amber-50"
                          : "border-gray-100 bg-white";

                    return (
                      <div
                        key={paymentOrder.id}
                        onClick={handleOrderClicked(
                          paymentOrder.id.toString(),
                          paymentOrder.payment_stage_id,
                        )}
                        className={`rounded-xl shadow-md border p-4 hover:shadow-lg transition-all duration-200 cursor-pointer ${cardBorderClass}`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              {paymentOrder.store_name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {paymentOrder.po_number ||
                                `ORD-${paymentOrder.id}`}
                            </p>
                          </div>
                          {/* Show deadline badge for pending tabs, nothing for completed */}
                          {activeTab !== 2 ? (
                            getMobileDeadlineBadge(daysRemaining)
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Completed
                            </span>
                          )}
                        </div>

                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-gray-500">
                              {paymentOrder.supplier_name}
                            </p>
                            <p className="text-sm text-gray-400">
                              {renderMobileDateInfo(paymentOrder)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-700">
                              {formatCurrency(paymentOrder.total_cost)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DataList>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        )}
      </div>

      {/* Floating Batch Action Bar */}
      {canSelectOrders && selectedOrderIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-20 bg-green-700 text-white rounded-xl shadow-2xl px-6 py-4 flex items-center space-x-6 animate-slide-up">
          <div className="flex items-center space-x-3">
            <Checkbox
              size="small"
              checked={selectedOrderIds.size === orders.length}
              indeterminate={
                selectedOrderIds.size > 0 &&
                selectedOrderIds.size < orders.length
              }
              onChange={handleToggleAll}
              sx={{
                color: "white",
                "&.Mui-checked": { color: "white" },
                "&.MuiCheckbox-indeterminate": { color: "white" },
              }}
            />
            <span className="font-medium">
              {selectedOrderIds.size} order
              {selectedOrderIds.size > 1 ? "s" : ""} selected
            </span>
          </div>

          <div className="h-6 w-px bg-green-500" />

          <span className="font-bold text-lg">
            {formatCurrency(selectedTotal)}
          </span>

          <Button
            variant="contained"
            onClick={() => setIsBatchModalOpen(true)}
            sx={{
              backgroundColor: "white",
              color: "#15803d",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#f0fdf4" },
            }}
            startIcon={<FaMoneyBillWave />}
          >
            Pay Selected ({selectedOrderIds.size})
          </Button>

          <button
            onClick={() => setSelectedOrderIds(new Set())}
            className="text-green-200 hover:text-white text-sm underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Filter Popover */}
      <OrderFilter
        anchor={openFilter}
        onClose={() => setOpenFilter(null)}
        hideStoreFilter
        filter={(data: DataFilterData | string) => {
          if (typeof data !== "string") {
            const formatDate = (d: any) => {
              if (!d) return null;
              if (d instanceof Date) {
                return d.toISOString().split("T")[0];
              }
              return d;
            };

            const params = {
              page_no: null,
              per_page: perPage,
              order_by: orderBy,
              order: order,
              search: search,
              date_type: data.type ?? null,
              start_date: formatDate(data.start),
              end_date: formatDate(data.end),
            };
            const queryParams = createQueryParams(params);
            navigate({
              pathname: "",
              search: queryParams,
            });
          } else {
            navigate({
              pathname: "",
            });
          }
        }}
      />

      {/* Payment Process Modal */}
      <PaymentProcessModal
        open={isProcessModalOpen}
        onClose={handleCloseModal}
        currentTab={currentPaymentTab}
      />

      {/* Batch Payment Modal */}
      <BatchPaymentModal
        open={isBatchModalOpen}
        onClose={() => {
          setIsBatchModalOpen(false);
          setSelectedOrderIds(new Set());
          // Refresh the list
          dispatch(
            getPaymentOrders({
              tab: activeTab,
              search: search ?? undefined,
              view: isSeller ? "selling" : "order",
              pageNo: Number(pageNo) || 0,
              perPage: Number(perPage) || 25,
              orderBy: orderBy ?? "actual_delivery_date",
              order: order ?? "desc",
              dateType: dateType ?? undefined,
              startDate: startDate ?? undefined,
              endDate: endDate ?? undefined,
            }),
          );
        }}
        selectedOrders={selectedOrders}
      />

      {/* Batch Payment Confirmation/View Modal (tabs 1 & 2) */}
      <BatchPaymentConfirmationModal
        open={isBatchConfirmModalOpen}
        onClose={() => {
          setIsBatchConfirmModalOpen(false);
          setSelectedBatchId(null);
          // Refresh the list
          dispatch(
            getPaymentOrders({
              tab: activeTab,
              search: search ?? undefined,
              view: isSeller ? "selling" : "order",
              pageNo: Number(pageNo) || 0,
              perPage: Number(perPage) || 25,
              orderBy: orderBy ?? "actual_delivery_date",
              order: order ?? "desc",
              dateType: dateType ?? undefined,
              startDate: startDate ?? undefined,
              endDate: endDate ?? undefined,
            }),
          );
        }}
        batchId={selectedBatchId}
        readOnly={batchModalReadOnly}
        isCompleted={batchModalIsCompleted}
      />
    </div>
  );
}
